/// DropdownMenu: Wraps Radix UI DropdownMenu with consistent dark styling.

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import type { ReactNode } from "react";

// ---------------------------------------------------------------------------
// Root and trigger (re-exported for convenience)
// ---------------------------------------------------------------------------

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

interface DropdownMenuContentProps {
  children: ReactNode;
  align?: "start" | "center" | "end";
  sideOffset?: number;
  className?: string;
}

export function DropdownMenuContent({
  children,
  align = "start",
  sideOffset = 4,
  className = "",
}: DropdownMenuContentProps) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        className={`z-50 min-w-[180px] rounded-md border border-zinc-700 bg-zinc-800 p-1 shadow-xl animate-in fade-in-0 zoom-in-95 ${className}`}
      >
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  );
}

// ---------------------------------------------------------------------------
// Item
// ---------------------------------------------------------------------------

interface DropdownMenuItemProps {
  children: ReactNode;
  shortcut?: string;
  disabled?: boolean;
  onSelect?: () => void;
  className?: string;
}

export function DropdownMenuItem({
  children,
  shortcut,
  disabled = false,
  onSelect,
  className = "",
}: DropdownMenuItemProps) {
  return (
    <DropdownMenuPrimitive.Item
      disabled={disabled}
      onSelect={onSelect}
      className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm text-zinc-200 outline-none cursor-default select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-zinc-700 data-[highlighted]:text-zinc-100 ${className}`}
    >
      <span className="flex-1 flex items-center gap-2">{children}</span>
      {shortcut && <span className="ml-auto text-xs text-zinc-500">{shortcut}</span>}
    </DropdownMenuPrimitive.Item>
  );
}

// ---------------------------------------------------------------------------
// Separator
// ---------------------------------------------------------------------------

export function DropdownMenuSeparator() {
  return <DropdownMenuPrimitive.Separator className="my-1 h-px bg-zinc-700" />;
}

// ---------------------------------------------------------------------------
// Label (available via DropdownMenuPrimitive.Label if needed)
// ---------------------------------------------------------------------------
